import ClientItem from 'owa-mail-store/lib/store/schema/ClientItem';
import getCurrentSelectedTableView from 'mini-mail-list-store/lib/utils/getSelectedTableView';
import isItemView from 'owa-mail-store/lib/utils/isItemView';
import { CalendarEvent } from 'mini-calendar-event-types';
import { getCustomPayloadForAppRenderBootAnalysis } from 'bootstrap/lib/getCustomPayloadForAppRenderBootAnalysis';
import { getTimestamp, now } from 'owa-datetime';
import {
    getTimeToFetchConflictingMeetings,
    getTimeToFetchMeetingMessage,
} from 'mini-meeting-invite/lib/store/Store';

let renderOrder = 0;

export const getCustomDataForLogging = async (
    meetingClientItem: ClientItem,
    calEvent: CalendarEvent,
    initialRenderTimestamp: number,
    partialRenderTimestamp: number,
    lazyLoadTime: number
) => {
    const isRecurringOrCanceled = calEvent?.IsRecurring || calEvent?.IsCancelled;
    const isProgressiveRender = !isRecurringOrCanceled && partialRenderTimestamp;
    const isRemove = calEvent?.canRemove();
    const currentTimestamp = getTimestamp(now());
    const totalRenderTime = currentTimestamp - initialRenderTimestamp;
    const meetingClientItemId = meetingClientItem?.ItemId?.Id;
    const timestampToFetchMeetingMessage = getTimeToFetchMeetingMessage(meetingClientItemId);
    const timestampToFetchConflictingMeetings = getTimeToFetchConflictingMeetings(
        meetingClientItemId
    );

    // Service call timing
    const timeTakenToFetchMeetingMessage =
        timestampToFetchMeetingMessage?.endTimestamp -
        timestampToFetchMeetingMessage?.startTimestamp;
    const perceivedTimeToFetchMeetingMessage = Math.max(
        timestampToFetchMeetingMessage?.endTimestamp - initialRenderTimestamp,
        0
    );

    const timeTakenToFetchConflictingMeetings = isRecurringOrCanceled
        ? 0
        : timestampToFetchConflictingMeetings?.endTimestamp -
          timestampToFetchConflictingMeetings?.startTimestamp;

    const perceivedTimeToFetchConflictingMeetings =
        isRecurringOrCanceled ||
        initialRenderTimestamp > timestampToFetchConflictingMeetings?.endTimestamp
            ? 0
            : timestampToFetchConflictingMeetings?.endTimestamp - initialRenderTimestamp;

    const lazyProcessingTimeForRsvp =
        timestampToFetchMeetingMessage?.timestampPostRsvpProcessing -
        timestampToFetchMeetingMessage?.endTimestamp;

    //
    const perceivedLazyProcessingTimeForRsvp = Math.max(
        0,
        Math.min(
            lazyProcessingTimeForRsvp,
            /**
             * when initialRenderTimestamp value is between
             * timestampPostRsvpProcessing and timestampToFetchMeetingMessage?.endTimestamp value
             */
            timestampToFetchMeetingMessage?.timestampPostRsvpProcessing - initialRenderTimestamp
        )
    );

    // Network and app boot info
    const payloadForAppRenderBootAnalysis = await getCustomPayloadForAppRenderBootAnalysis();

    // Index of Rsvp in List view from the top
    const indexOfRsvp = getIndexOfRsvpInListView(meetingClientItem);

    ++renderOrder;

    return {
        ...payloadForAppRenderBootAnalysis,

        /**
         * Time taken to lazy load list view rsvp unit
         */
        LzLdLVRU: lazyLoadTime,

        /**
         * Time taken to complete fetching meeting message basic info
         */
        fmm: timeTakenToFetchMeetingMessage,

        /**
         * Time taken to complete fetching meeting message basic info keeping
         * the rsvp unit mount as start point. This is added to get the perceived time
         * of the service call.
         */
        pfmm: perceivedTimeToFetchMeetingMessage,

        /**
         * lazy processing time of Rsvp
         */
        lpt: lazyProcessingTimeForRsvp,

        /**
         * perceived lazy processing time of Rsvp
         */
        plpt: perceivedLazyProcessingTimeForRsvp,

        /**
         * partial render time of Rsvp
         */
        prt: partialRenderTimestamp
            ? partialRenderTimestamp - initialRenderTimestamp
            : totalRenderTime,

        /**
         * Time taken to complete fetching conflicting meetings info
         */
        fcm: timeTakenToFetchConflictingMeetings,

        /**
         * Time taken to complete fetching conflicting meetings info keeping
         * the rsvp unit mount as start point. This is added to get the perceived time of the service call.
         */
        pfcm: perceivedTimeToFetchConflictingMeetings,

        /**
         * complete render time of Rsvp
         */
        crt: isProgressiveRender ? currentTimestamp - partialRenderTimestamp : 0,

        /**
         * Time taken to render the Rsvp card completely from the start including the lazy load time
         */
        trt: totalRenderTime,
        calendarItemType: calEvent?.CalendarItemType,
        isCanceled: calEvent?.IsCancelled,
        IsEffectiveAllDayEvent: calEvent?.IsEffectiveAllDayEvent,
        itemId: calEvent?.ItemId?.Id,
        conflictingMeetingCount: calEvent?.ActualConflictingMeetingCount,
        renderOrder: renderOrder,
        ...(isRemove !== undefined && { rsvpOrRemove: isRemove ? 'Remove' : 'Rsvp' }),
        indexOfRsvp,
    };
};

const getIndexOfRsvpInListView = (meetingClientItem: ClientItem) => {
    const meetingClientItemId = meetingClientItem?.ItemId?.Id;
    const meetingClientConversationId = meetingClientItem?.ConversationId?.Id;

    const currentSelectedTableView = getCurrentSelectedTableView();
    const currentSelectedTableViewItemIds = currentSelectedTableView.itemIds;
    const mailListItemId = isItemView(currentSelectedTableView)
        ? meetingClientItemId
        : meetingClientConversationId;

    return currentSelectedTableViewItemIds.findIndex((itemId: string) => itemId === mailListItemId);
};
